import type { DungeonMap, GridCell, Position } from "../types/grid.js";
import { WorldStore } from "../../store/WorldStore.js";

/**
 * Runtime wrapper around a loaded DungeonMap.
 * Provides O(1) cell lookups and edge-wall queries.
 */
export class GridMap {
  private readonly cells: Map<string, GridCell>;
  /** Maps grid-cell key → door ID for all door objects on this map. */
  private readonly doors: Map<string, string> = new Map();
  /** Cells blocked by free-standing props / containers (W19). */
  private readonly solidProps: Set<string> = new Set();

  constructor(private readonly map: DungeonMap) {
    this.cells = new Map(map.cells.map((cell) => [GridMap.key(cell.position), cell]));
  }

  /**
   * Register door objects from the Tiled object layer.
   * Call once after map load, before any movement checks.
   *
   * @param doorCells  Array of { position, doorId } extracted from TiledLoader metadata.
   */
  registerDoors(doorCells: Array<{ position: Position; doorId: string }>): void {
    for (const { position, doorId } of doorCells) {
      this.doors.set(GridMap.key(position), doorId);
    }
  }

  /**
   * Mark cells impassable due to free-standing props / containers (W19).
   * Call once after map load, alongside `registerDoors`.
   */
  registerSolidProps(cells: readonly Position[]): void {
    for (const pos of cells) {
      this.solidProps.add(GridMap.key(pos));
    }
  }

  get width(): number {
    return this.map.width;
  }

  get height(): number {
    return this.map.height;
  }

  static key(pos: Position): string {
    return `${pos.x},${pos.z}`;
  }

  getCell(pos: Position): GridCell | undefined {
    return this.cells.get(GridMap.key(pos));
  }

  isPassable(pos: Position): boolean {
    const cell = this.getCell(pos);
    if (!cell) return false;
    if (cell.solid) return false;

    // Closed doors block movement until opened
    const doorId = this.doors.get(GridMap.key(pos));
    if (doorId !== undefined && !WorldStore.isDoorOpen(doorId)) {
      return false;
    }

    // Free-standing props / containers that opt into blocks_movement.
    if (this.solidProps.has(GridMap.key(pos))) return false;

    return true;
  }

  isInBounds(pos: Position): boolean {
    return pos.x >= 0 && pos.x < this.map.width && pos.z >= 0 && pos.z < this.map.height;
  }
}
